<script setup>
    import { ref, onMounted, defineProps, computed } from 'vue';
    import { useStore } from 'vuex';

    import Alerts from '@/api/alerts';
    import GenericTable2 from '@/components/tables/GenericTable2.vue';
    import CreateAlertModal from '@/components/modals/CreateAlert.vue';
    import ConfirmModal from '@/components/modals/ConfirmModal.vue';

    const props = defineProps({
        dataID: {
            type: String,
            default: null
        }
    });

    const loading = ref(true);
    const editingAlert = ref(false);
    const myAlertsRaw = ref([]);
    const myAlerts = ref([]);
    const sharedWithMeRaw = ref([]);
    const sharedWithMe = ref([]);
    const allAlertsRaw = ref([]);
    const allAlerts = ref([]);
    const activeTab = ref('owned');
    const selectedMyAlerts = ref([]);
    const selectedSharedAlerts = ref([]);
    const selectedAllAlerts = ref([]);
    const modalOpen = ref(false);
    const alertDetails = ref(null);
    const pendingDelete = ref(null); // { ids: string[], label: 'delete'|'unsubscribe' }
    const deleteLoading = ref(false);
    const deleteError = ref('');
    const store = useStore();
    const user = store.state.user;
    
    // Permission checks
    const canEditAllAlerts = computed(() => {
        return user && (
            user.role === 'admin' || 
            (user.permissions && user.permissions.includes('editAllAlerts'))
        );
    });
    
    const canDeleteAllAlerts = computed(() => {
        return user && (
            user.role === 'admin' || 
            (user.permissions && user.permissions.includes('deleteAllAlerts'))
        );
    });

    const canViewAllAlerts = computed(() => {
        return user && (
            user.role === 'admin' || 
            (user.permissions && (
                user.permissions.includes('viewAllAlerts') ||
                user.permissions.includes('editAllAlerts') ||
                user.permissions.includes('deleteAllAlerts')
            ))
        );
    });

    // Computed property for all alerts table actions
    const allAlertsActions = computed(() => {
        const actions = [];
        if (canEditAllAlerts.value) actions.push('edit');
        if (canDeleteAllAlerts.value) actions.push('delete');
        return actions;
    });

    onMounted(async () => {
        await getAlerts();
        loading.value = false;
    })

    const getAlerts = async () => {
        let response;
        if (props.dataID) {
            response = await Alerts.get(`?dataID=${props.dataID}`);
        }
        else {
            response = await Alerts.get();
        }
        if (response.status === 'success') {
            const alerts = response.details;

            allAlertsRaw.value = alerts;

            // filter alerts by createdBy
            myAlertsRaw.value = alerts.filter(alert => alert.owners.includes(user.uid));
            sharedWithMeRaw.value = alerts.filter(alert => alert.users.includes(user.uid) && !alert.owners.includes(user.uid));


            const formatAlert = (alert) => ({
                id: alert.id,
                name: alert.name,
                "Alerts Every": alert.interval === 0 ? 'Once per event' : `${Math.floor(alert.interval/86400)} days, ${Math.floor((alert.interval%86400)/3600)} hours, ${Math.floor((alert.interval%3600)/60)} minutes`,
                description: alert.description,
                status: alert.status === 'high' ? 'Triggered' : 'OK'
            });

            allAlerts.value = allAlertsRaw.value.map(formatAlert);
            myAlerts.value = myAlertsRaw.value.map(formatAlert);
            sharedWithMe.value = sharedWithMeRaw.value.map(formatAlert);
        }
    }

    const alertAction = async (alertDetails) => {
        // Edit existing alert
        if (editingAlert.value) {
            await Alerts.put(alertDetails);
            editingAlert.value = false;
        }
        
        // Create new alert
        else {
            alertDetails.createdBy = user.uid;
            await Alerts.post(alertDetails);
        }
        modalOpen.value = false;
        await getAlerts();
    }

    const editAlert = (alerts) => {
        editingAlert.value = true;
        alertDetails.value = alerts[0];
        modalOpen.value = true;
    }

    const requestDelete = (ids, label = 'delete') => {
        pendingDelete.value = { ids, label };
        deleteError.value = '';
    };

    const confirmDelete = async () => {
        deleteLoading.value = true;
        deleteError.value = '';
        try {
            await Alerts.delete({ alertID: pendingDelete.value.ids });
            pendingDelete.value = null;
            await getAlerts();
        } catch {
            deleteError.value = 'Something went wrong. Please try again.';
        } finally {
            deleteLoading.value = false;
        }
    };

    const cancelDelete = () => {
        pendingDelete.value = null;
        deleteError.value = '';
    };

    const myAlertsSelected = (rows) => {  
        selectedMyAlerts.value = rows;
    }

    const sharedAlertsSelected = (rows) => {
        selectedSharedAlerts.value = rows;
    }

    const allAlertsSelected = (rows) => {
        selectedAllAlerts.value = rows;
    }

    const actionSelected = (details) => {
        const alertData = allAlertsRaw.value.find(alert => alert.id === details.row.id);
        
        // Check if this is from "All Alerts" view or personal alerts
        const isAllAlertsView = activeTab.value === 'all';
        
        if (details.action === 'edit') {
            // For "All Alerts" view, check editAllAlerts permission
            if (isAllAlertsView && !canEditAllAlerts.value) {
                return;
            }
            editingAlert.value = true;
            alertDetails.value = alertData;
            modalOpen.value = true;
        }
        else if (details.action === 'delete') {
            if (isAllAlertsView && !canDeleteAllAlerts.value) return;
            requestDelete([alertData.id]);
        }
        else if (details.action === 'unsubscribe') {
            requestDelete([alertData.id], 'unsubscribe');
        }
    }
</script>

<template>
    <!-- Header row: tab bar left, add button right -->
    <div class="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div class="inline-flex border-2 border-mc-red rounded-full overflow-hidden">
            <button
                @click="activeTab = 'owned'"
                :class="activeTab === 'owned' ? 'bg-mc-red text-white' : 'bg-transparent text-mc-red dark:text-red-400'"
                class="px-5 py-2 font-bold text-sm whitespace-nowrap"
            >Owned</button>
            <button
                @click="activeTab = 'shared'"
                :class="activeTab === 'shared' ? 'bg-mc-red text-white' : 'bg-transparent text-mc-red dark:text-red-400'"
                class="px-5 py-2 font-bold text-sm whitespace-nowrap border-l-2 border-mc-red"
            >Shared With Me</button>
            <button
                v-if="canViewAllAlerts"
                @click="activeTab = 'all'"
                :class="activeTab === 'all' ? 'bg-mc-red text-white' : 'bg-transparent text-mc-red dark:text-red-400'"
                class="px-5 py-2 font-bold text-sm whitespace-nowrap border-l-2 border-mc-red"
            >All Alerts</button>
        </div>
        <button @click="editingAlert = false; alertDetails = null; modalOpen = true" class="default-button">+ add alert</button>
    </div>

    <div v-if="activeTab === 'owned'" class="mb-8 px-2 md:px-0 md:mr-10">
        <div class="border border-gray-800 dark:border-gray-200 rounded-md p-3 md:p-5">
            <div class="flex flex-col sm:flex-row justify-between gap-3 mb-5">
                <h2 class="text-xl md:text-2xl font-bold">Owned Alerts</h2>
                <div v-if="selectedMyAlerts.length > 0" class="flex gap-2">
                    <button v-if="selectedMyAlerts.length === 1"
                            @click="editAlert(selectedMyAlerts.map(index => myAlertsRaw[index]))"
                            class="default-button">
                        edit
                    </button>
                    <button @click="requestDelete(selectedMyAlerts.map(index => myAlertsRaw[index].id))"
                            class="default-button">
                        delete
                    </button>
                </div>
            </div>
            <div class="overflow-x-auto -mx-3 md:mx-0">
                <div class="min-w-full px-3 md:px-0">
                    <GenericTable2
                        :loading="loading"
                        :data="myAlerts"
                        :actions="['edit', 'delete']"
                        :selectable="true"
                        :pagination="true"
                        :showId="false"
                        @RowsSelected="myAlertsSelected"
                        @ActionSelected="actionSelected"
                    />
                </div>
            </div>
        </div>
    </div>

    <div v-if="activeTab === 'shared'" class="mb-8 px-2 md:px-0 md:mr-10">
        <div class="border border-gray-800 dark:border-gray-200 rounded-md p-3 md:p-5">
            <div class="flex flex-col sm:flex-row justify-between gap-3 mb-5">
                <h2 class="text-xl md:text-2xl font-bold">Alerts Shared With Me</h2>
                <div v-if="selectedSharedAlerts.length > 0" class="flex gap-2">
                    <button @click="requestDelete(selectedSharedAlerts.map(index => sharedWithMeRaw[index].id), 'unsubscribe')"
                            class="default-button">
                        unsubscribe
                    </button>
                </div>
            </div>
            <div class="overflow-x-auto -mx-3 md:mx-0">
                <div class="min-w-full px-3 md:px-0">
                    <GenericTable2
                        :loading="loading"
                        :data="sharedWithMe"
                        :actions="['unsubscribe']"
                        :selectable="true"
                        :pagination="true"
                        :showId="false"
                        @RowsSelected="sharedAlertsSelected"
                        @ActionSelected="actionSelected"
                    />
                </div>
            </div>
        </div>
    </div>

    <div v-if="activeTab === 'all' && canViewAllAlerts" class="mb-8 px-2 md:px-0 md:mr-10">
        <div class="border border-gray-800 dark:border-gray-200 rounded-md p-3 md:p-5">
            <div class="flex flex-col sm:flex-row justify-between gap-3 mb-5">
                <div>
                    <h2 class="text-xl md:text-2xl font-bold">All Alerts</h2>
                    <p v-if="!canEditAllAlerts && !canDeleteAllAlerts" class="text-sm text-orange-600 dark:text-orange-400 mt-1">
                        Read-only view - You need 'editAllAlerts' or 'deleteAllAlerts' permission to modify alerts
                    </p>
                    <p v-else-if="!canEditAllAlerts" class="text-sm text-orange-600 dark:text-orange-400 mt-1">
                        Limited access - You need 'editAllAlerts' permission to edit alerts
                    </p>
                    <p v-else-if="!canDeleteAllAlerts" class="text-sm text-orange-600 dark:text-orange-400 mt-1">
                        Limited access - You need 'deleteAllAlerts' permission to delete alerts
                    </p>
                </div>
                <div v-if="(canEditAllAlerts || canDeleteAllAlerts) && selectedAllAlerts.length > 0" class="flex gap-2">
                    <button v-if="canEditAllAlerts && selectedAllAlerts.length === 1"
                            @click="editAlert(selectedAllAlerts.map(index => allAlertsRaw[index]))"
                            class="default-button">
                        edit
                    </button>
                    <button v-if="canDeleteAllAlerts"
                            @click="requestDelete(selectedAllAlerts.map(index => allAlertsRaw[index].id))"
                            class="default-button">
                        delete
                    </button>
                </div>
            </div>
            <div class="overflow-x-auto -mx-3 md:mx-0">
                <div class="min-w-full px-3 md:px-0">
                    <GenericTable2
                        :loading="loading"
                        :data="allAlerts"
                        :actions="allAlertsActions"
                        :selectable="canEditAllAlerts || canDeleteAllAlerts"
                        :pagination="true"
                        :showId="false"
                        @RowsSelected="allAlertsSelected"
                        @ActionSelected="actionSelected"
                    />
                </div>
            </div>
        </div>
    </div>

    <CreateAlertModal v-if="modalOpen"
        :dataID="dataID"
        :alert-details="alertDetails"
        @alert-details="alertAction"
        @close="modalOpen = false, alertDetails = null"
    />

    <ConfirmModal
        v-if="pendingDelete"
        :title="pendingDelete.label === 'unsubscribe' ? 'Unsubscribe from alert?' : (pendingDelete.ids.length === 1 ? 'Delete alert?' : `Delete ${pendingDelete.ids.length} alerts?`)"
        :message="pendingDelete.label === 'unsubscribe' ? 'You will stop receiving notifications for this alert. The alert itself will not be deleted.' : 'This cannot be undone.'"
        :confirm-label="pendingDelete.label === 'unsubscribe' ? 'Unsubscribe' : 'Delete'"
        :loading="deleteLoading"
        :error="deleteError"
        variant="danger"
        @confirm="confirmDelete"
        @cancel="cancelDelete"
    />
</template>
